import { useCallback, useEffect, useState } from "react";

import { api } from "../api";
import { ActionsPanel } from "../components/ActionsPanel";
import { formatDate } from "../config";
import { priorityOf } from "../nextstep";
import type { ActionView, Client, Priority } from "../types";
import { liveClients } from "../types";
import { ItemLink } from "../components/ItemLink";

/**
 * What has a next step still to do, worst first.
 *
 * The server decides the order and computes `overdue`, so this file renders rather than
 * reasons — the digest email has no browser and must agree with this screen about what is
 * late. Grouping is by band rather than by date: her question on opening this is "what have
 * I missed and what is next", not "what is the chronology".
 */

type Band = "overdue" | "soon" | "later" | "unresolved" | "undated";

const BAND_LABELS: Record<Band, string> = {
  overdue: "Overdue",
  soon: "Next 14 days",
  later: "Later",
  unresolved: "Deadline needs resolving",
  undated: "No deadline",
};

const BAND_ORDER: Band[] = ["overdue", "soon", "later", "unresolved", "undated"];

function bandOf(item: ActionView): Band {
  if (item.due_date) {
    if (item.overdue) return "overdue";
    return (item.days_until ?? 999) <= 14 ? "soon" : "later";
  }
  if (item.ai_due_date_status === "unresolved") return "unresolved";
  return "undated";
}

function dueLabel(item: ActionView): string {
  if (!item.due_date) return "";
  const days = item.days_until;
  if (days === null || days === undefined) return formatDate(item.due_date);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days < 0) return `${Math.abs(days)} days ago`;
  return `in ${days} days`;
}

/**
 * How much an action matters, which is a different question from when it is due.
 *
 * The date-led bands still decide the order — a thing due today is read before a thing due
 * next month whatever either is marked — and priority decides what she looks at *inside* a
 * band, or filters the screen down to. Sorting by it instead would put a low-priority item
 * due today under a high-priority one due in a fortnight, which is the wrong list.
 *
 * Unset reads as medium and is not stored as medium: every item predating the field is
 * unset, and writing "medium" across the log would claim she had triaged things she has
 * never opened.
 */
const PRIORITIES: Priority[] = ["high", "medium", "low"];

const PRIORITY_LABELS: Record<Priority, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};



function PriorityPicker({
  item,
  saving,
  onSet,
}: {
  item: ActionView;
  saving: boolean;
  onSet: (value: Priority) => void;
}) {
  const current = priorityOf(item);
  return (
    <label className={`priority priority-${current}`}>
      <span className="visually-hidden">Priority for {item.title}</span>
      <select
        value={current}
        disabled={saving}
        onChange={(event) => onSet(event.target.value as Priority)}
      >
        {PRIORITIES.map((value) => (
          <option key={value} value={value}>
            {PRIORITY_LABELS[value]}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * A due date she can pick or type, applied when she says so.
 *
 * It used to patch on every `onChange`, which broke it two ways and both were reported.
 * A native date input fires `change` as soon as a day is clicked, so the patch set `saving`,
 * `saving` disabled the input, and the browser closed the picker out from under her — "it
 * exits out the control as soon as you select anything". Typing was worse: day, month and
 * year each fire their own change, so every keystroke patched a half-finished date and the
 * re-render threw away what she had typed.
 *
 * So the value is local while she works and committed once — on Enter, on blur, or on Set.
 * The button is not decoration: a native picker has no OK of its own in most browsers, and
 * an explicit apply is the only version of this control that behaves the same in all of them.
 */
function DueDate({
  item,
  saving,
  onApply,
}: {
  item: ActionView;
  saving: boolean;
  onApply: (value: string) => void;
}) {
  const saved = item.due_date || "";
  const [value, setValue] = useState(saved);

  // Follow the server when it changes underneath us (an accepted suggestion, a reload) —
  // but never while she is mid-edit, or this is the same bug in a different costume.
  useEffect(() => {
    setValue(saved);
  }, [saved]);

  const dirty = value !== saved;
  const apply = () => {
    if (dirty) onApply(value);
  };

  return (
    <span className="due-entry">
      <input
        type="date"
        value={value}
        disabled={saving}
        onChange={(event) => setValue(event.target.value)}
        onBlur={apply}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            apply();
          } else if (event.key === "Escape") {
            setValue(saved);
          }
        }}
        aria-label={`Due date for ${item.title}`}
      />
      {dirty && (
        <button type="button" className="accept" disabled={saving} onClick={apply}>
          Set
        </button>
      )}
    </span>
  );
}

export function ActionsView({
  clients,
  onError,
  onCountChange,
}: {
  clients: Client[];
  onError: (message: string) => void;
  onCountChange?: (count: number) => void;
}) {
  const [items, setItems] = useState<ActionView[]>([]);
  const [counts, setCounts] = useState({ open: 0, overdue: 0, unresolved: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [showComplete, setShowComplete] = useState(false);
  const [only, setOnly] = useState<Priority | "">("");
  /**
   * The item whose actions she is editing, or null.
   *
   * This is where Joanne asked the question — *"I say something is done in the actions… but
   * then I want to action it for another client"* — so it is where the answer has to be. The
   * same panel the log opens, over the row she is looking at.
   */
  const [editing, setEditing] = useState<ActionView | null>(null);
  // Collapsed rather than expanded: the bands she opens this screen for are the top ones, and
  // a screen that remembers nothing is a screen that opens the same way every morning.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (band: string) =>
    setCollapsed((was) => {
      const next = new Set(was);
      if (next.has(band)) next.delete(band);
      else next.add(band);
      return next;
    });

  const load = useCallback(() => {
    setLoading(true);
    api
      .listActions(showComplete)
      .then((page) => {
        setItems(page.items);
        // Every one of these is over what is still to do, whichever list she asked to see —
        // the server counts them that way now. It used to count over the rows it returned,
        // so ticking "Show completed" made the tab badge and the summary climb, and the
        // guard here existed to hide half of that. A count she can only correct by
        // unticking a box is a count that lies while the screen looks fine.
        setCounts({ open: page.open, overdue: page.overdue, unresolved: page.unresolved });
        onCountChange?.(page.open);
      })
      .catch((err: Error) => onError(err.message))
      .finally(() => setLoading(false));
  }, [showComplete, onError, onCountChange]);

  useEffect(load, [load]);

  /**
   * Change the item behind an action — today only its priority.
   *
   * Kept apart from `patchAction` below rather than merged into one "save" that guesses which
   * it meant. Priority is a property of the publication: it is the same however many pieces of
   * work she has on it, and marking one of them done does not make the publication matter less.
   */
  const patchItem = useCallback(
    async (item: ActionView, changes: Parameters<typeof api.patchItem>[1]) => {
      setSaving(item.action_id);
      try {
        await api.patchItem(item.id, changes);
        load();
      } catch (err) {
        onError((err as Error).message);
      } finally {
        setSaving(null);
      }
    },
    [load, onError],
  );

  /** Change one action — its date, its status, or whose work it is. */
  const patch = useCallback(
    async (item: ActionView, changes: Parameters<typeof api.patchAction>[2]) => {
      setSaving(item.action_id);
      try {
        await api.patchAction(item.id, item.action_id, changes);
        load();
      } catch (err) {
        onError((err as Error).message);
      } finally {
        setSaving(null);
      }
    },
    [load, onError],
  );

  if (loading) return <p className="muted">Loading actions…</p>;

  if (!items.length) {
    return (
      <div className="actions">
        <p className="muted">Nothing outstanding. Every next step is done.</p>
      </div>
    );
  }

  const shown = only ? items.filter((item) => priorityOf(item) === only) : items;

  // Bands keep their own totals as well as the filtered count, so narrowing to High never
  // reads as "the overdue pile shrank".
  const grouped = BAND_ORDER.map((band) => ({
    band,
    items: shown.filter((item) => bandOf(item) === band),
    total: items.filter((item) => bandOf(item) === band).length,
  })).filter((group) => group.total);

  return (
    <div className="actions">
      <header className="actions-summary">
        <span>
          <strong>{counts.open}</strong> open
          {showComplete && items.length !== counts.open && (
            <span className="muted"> of {items.length} shown</span>
          )}
        </span>
        {counts.overdue > 0 && (
          <span className="overdue">
            <strong>{counts.overdue}</strong> overdue
          </span>
        )}
        {counts.unresolved > 0 && (
          <span className="unresolved">
            <strong>{counts.unresolved}</strong> needing a date
          </span>
        )}
        <label className="priority-filter">
          Priority
          <select value={only} onChange={(e) => setOnly(e.target.value as Priority | "")}>
            <option value="">All</option>
            {PRIORITIES.map((value) => (
              <option key={value} value={value}>
                {PRIORITY_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        <label className="show-complete">
          <input
            type="checkbox"
            checked={showComplete}
            onChange={(e) => setShowComplete(e.target.checked)}
          />
          Show completed
        </label>
      </header>

      {grouped.map(({ band, items: rows, total }) => (
        <section key={band} className={`action-band band-${band}`}>
          <h3>
            <button
              type="button"
              className="band-toggle"
              aria-expanded={!collapsed.has(band)}
              onClick={() => toggle(band)}
            >
              <span className="disclosure" aria-hidden="true">
                {collapsed.has(band) ? "▸" : "▾"}
              </span>
              {BAND_LABELS[band]}
              <span className="band-count">
                {only && rows.length !== total ? `${rows.length} of ${total}` : total}
              </span>
            </button>
          </h3>
          {collapsed.has(band) && only && rows.length === 0 && (
            <p className="small muted band-empty">
              Nothing at this priority in {BAND_LABELS[band].toLowerCase()}.
            </p>
          )}
          {!collapsed.has(band) && rows.length === 0 && (
            <p className="small muted band-empty">
              Nothing at this priority — {total} here at other priorities.
            </p>
          )}
          {!collapsed.has(band) && rows.map((item) => (
            <article key={item.action_id} className="action">
              <div className="action-head">
                <ItemLink url={item.url}>{item.title}</ItemLink>
                <span className="meta">
                  {item.regulator}
                  {item.clients.length > 0 && ` · ${item.clients.join(", ")}`}
                  {item.norwood_action && ` · ${item.norwood_action}`}
                </span>
              </div>

              <p className="next-step">
                {item.client ? <span className="action-client">{item.client}</span> : null}
                {item.next_steps}
              </p>

              <div className="action-due">
                {item.due_date ? (
                  <span className={item.overdue ? "due overdue" : "due"}>
                    Due {formatDate(item.due_date)} ({dueLabel(item)})
                  </span>
                ) : item.ai_due_date_status === "unresolved" ? (
                  /* A deadline she can date in seconds. Showing her own words is the whole
                     point — she resolves it without opening the source. */
                  <span className="due unresolved">
                    Deadline not dated — “{item.ai_due_date_basis}”
                  </span>
                ) : (
                  <span className="due muted">No deadline</span>
                )}

                {/* A suggested date is only ever offered, never applied. */}
                {!item.due_date && item.ai_due_date && (
                  <button
                    type="button"
                    className="accept"
                    disabled={saving === item.action_id}
                    title={item.ai_due_date_basis}
                    onClick={() => patch(item, { due_date: item.ai_due_date })}
                  >
                    Use {formatDate(item.ai_due_date)}
                  </button>
                )}

                <DueDate
                  item={item}
                  saving={saving === item.action_id}
                  onApply={(value) => patch(item, { due_date: value })}
                />

                <PriorityPicker
                  item={item}
                  saving={saving === item.action_id}
                  onSet={(value) => patchItem(item, { priority: value })}
                />

                {item.next_steps_status !== "complete" ? (
                  <button
                    type="button"
                    disabled={saving === item.action_id}
                    onClick={() => patch(item, { next_steps_status: "complete" })}
                  >
                    Done
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={saving === item.action_id}
                    onClick={() => patch(item, { next_steps_status: "open" })}
                  >
                    Reopen
                  </button>
                )}

                {/* Her question, answered where she asked it. Marking this one done no longer
                    has to be undone to record work for somebody else — that is a new action,
                    and this is the way to one without leaving the screen. */}
                <button
                  type="button"
                  className="link"
                  disabled={saving === item.action_id}
                  onClick={() => setEditing(item)}
                >
                  Add another
                </button>
              </div>
            </article>
          ))}
        </section>
      ))}

      {editing && (
        <ActionsPanel
          itemId={editing.id}
          itemTitle={editing.title}
          live={liveClients(clients)}
          alsoNamed={editing.clients}
          onClose={() => setEditing(null)}
          onChanged={load}
          onError={(error) => onError(error instanceof Error ? error.message : String(error))}
        />
      )}
    </div>
  );
}
